import React, { useEffect } from 'react';
import { Controller } from 'react-hook-form';
import { VigoTextInput } from '../vigo-textinput';
import { dynamicFormField } from 'src/types/apps/masterdataTypes';
import VigoAutoComplete from '../vigo-autocomplete';
import VigoDatePicker from '../vigo-datepicker';
import { VigoCheckBox } from '../vigo-checkbox';
import { useVigoTranslation } from 'src/@core/hooks/useVigoTranslation';

interface FormInputProps {
  control: any;
  setValue: any;
  fieldData: dynamicFormField;
  reset: any;
}

const systemColumnList = [
  'id',
  'createdOn',
  'createdBy',
  'updatedOn',
  'updatedBy',
  'isDeleted',
  'deletedBy',
  'deletedOn',
  'ownerTenantId',
  'ownerSiteId',
  'ownerPartnerId',
  'version'
  // 'masterDataId'
];

const FormInput = (props: FormInputProps) => {
  const { fieldData, control, setValue } = props;

  // Hooks
  const { t } = useVigoTranslation();

  useEffect(() => {
    setTimeout(() => setValue(fieldData.field, fieldData.value));
  }, [fieldData.field, fieldData.value, setValue]);

  // dont add system columns to edit form
  if (systemColumnList.includes(fieldData.field) || !fieldData.isVisible) {
    return <div></div>;
  }

  const inputProps = {
    maxLength: fieldData.maxlen,
    readOnly: fieldData.readonly
  };

  const required =
    (fieldData.minlen && fieldData.minlen > 0) || fieldData.field === 'code' || fieldData.field === 'description'
      ? true
      : false;

  switch (fieldData.type) {
    case 'string':
      return (
        <Controller
          key={fieldData.field}
          name={fieldData.field}
          control={control}
          defaultValue={fieldData.value}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <VigoTextInput
              name={fieldData.field}
              error={!!error}
              errormessage={error?.message}
              fullWidth
              required={required}
              sx={{ mb: 3 }}
              label={fieldData.label}
              value={value}
              onChange={onChange}
              inputProps={inputProps}
            />
          )}
        />
      );
    case 'number':
      return (
        <Controller
          key={fieldData.field}
          name={fieldData.field}
          control={control}
          defaultValue={fieldData.value}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <VigoTextInput
              name={fieldData.field}
              error={!!error}
              errormessage={error?.message}
              fullWidth
              required={required}
              sx={{ mb: 3 }}
              label={fieldData.label}
              value={value}
              onChange={onChange}
              type="number"
              inputProps={inputProps}
            />
          )}
        />
      );
    case 'lookup':
      return (
        <Controller
          key={fieldData.field}
          name={fieldData.field}
          control={control}
          defaultValue={fieldData.value}
          render={({ field: { onChange }, fieldState: { error } }) => (
            <VigoAutoComplete
              name={fieldData.field}
              error={!!error}
              errormessage={error?.message}
              allowAddNewOption={false}
              displayName={fieldData.lookupServiceDisplayName}
              fullWidth
              sx={{ mb: 6 }}
              disabled={fieldData.readonly}
              required={fieldData.required}
              label={fieldData.label}
              serviceLocation={fieldData.lookupServiceLocation ? fieldData.lookupServiceLocation : ''}
              serviceEndpoint={fieldData.lookupServiceEndPoint ? fieldData.lookupServiceEndPoint : ''}
              evaluateFilter={fieldData.evaluateFilter}
              clearDepenancies={fieldData.clearDependancies}
              dependancyProperty={fieldData.dependancyField}
              datasourceFilter={fieldData.filter}
              inputProps={inputProps}
              value={fieldData.value}
              onChange={(e, v) => {
                onChange(v);
              }}
            />
          )}
        />
      );
    case 'boolean': {
      const newFieldData = { ...fieldData };
      newFieldData.checked = Boolean(fieldData.value);
      const checkboxLabel = `${newFieldData.label?.replace(/([A-Z])/g, ' $1').trim()}?`;

      return (
        <Controller
          key={newFieldData.field}
          name={newFieldData.field}
          control={control}
          defaultValue={newFieldData.value}
          render={({ field: { value, onChange } }) => (
            <VigoCheckBox
              name={newFieldData.field}
              value={Boolean(value)}
              disabled={newFieldData.readonly}
              onChange={onChange}
              required={required}
              label={checkboxLabel}
              disableLabelTranslation
              labelSx={{ mb: 3, width: '100%', mr: 0, alignItems: 'center' }}
            />
          )}
        />
      );
    }
    case 'date':
      return (
        <Controller
          key={fieldData.field}
          name={fieldData.field}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <VigoDatePicker
              name={fieldData.field}
              error={!!error}
              errormessage={error?.message}
              fullWidth
              dateOnly
              onChange={field.onChange}
              label={fieldData.label?.replace(/([A-Z])/g, ' $1').trim()}
              sx={{ mb: 3 }}
              value={new Date(field.value) || ''}
              required={required}
            />
          )}
        />
      );

    default:
      return <div>{t('Unsupported field')}</div>;
  }
};

export default FormInput;
